import React, { useMemo } from "react";
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import { TrendingDown, TrendingUp, Minus } from "lucide-react";
import { formatShortINR, formatINR } from "@/lib/format";

// Canonical category → hex (mirrors backend CATEGORY_COLORS)
const CATEGORY_COLORS = {
  Dairy: "#FF6B00",
  Vegetables: "#1D9E75",
  Fruits: "#E24B4A",
  Grains: "#BA7517",
  Protein: "#378ADD",
  Beverages: "#534AB7",
  Snacks: "#D85A30",
  Household: "#888888",
  Organic: "#1D9E75",
  "Handling Charges": "#9D3CFF",
  Other: "#555566",
};

const TimelineTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  const total = payload.reduce((s, p) => s + (p.value || 0), 0);
  const sorted = [...payload].sort((a, b) => (b.value || 0) - (a.value || 0));
  return (
    <div className="bg-[#0A0A0F] border border-[#FF6B00] p-3 min-w-[220px]">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-2">
        {label}
      </div>
      <div className="space-y-1.5">
        {sorted.map((p) => (
          <div
            key={p.dataKey}
            className="flex items-center justify-between gap-4 text-xs font-mono-num"
          >
            <div className="flex items-center gap-2 min-w-0">
              <span className="w-2 h-2 shrink-0" style={{ background: p.color }} />
              <span className="text-[#F0EDE8] truncate">{p.dataKey}</span>
            </div>
            <span className="text-[#F0EDE8] shrink-0">{formatShortINR(p.value)}</span>
          </div>
        ))}
      </div>
      <div className="mt-2 pt-2 border-t border-[#2A2A35] flex justify-between text-xs font-mono-num">
        <span className="uppercase tracking-wider text-[#888]">Total</span>
        <span className="text-[#FF6B00] font-bold">{formatINR(total)}</span>
      </div>
    </div>
  );
};

/**
 * Stacked area chart of monthly spend × category.
 * Props: timeline = [{ month, label, total, <Category>: amount, ... }]
 */
export default function SpendTimeline({ timeline }) {
  const { data, categories, stats } = useMemo(() => {
    const rows = timeline || [];
    // Collect every category key across all rows (except metadata fields)
    const catSet = new Set();
    rows.forEach((r) => {
      Object.keys(r).forEach((k) => {
        if (!["month", "label", "total"].includes(k)) catSet.add(k);
      });
    });
    // Rank categories by grand total, keep top 6, roll the rest into "Other"
    const totals = {};
    rows.forEach((r) => {
      catSet.forEach((c) => {
        totals[c] = (totals[c] || 0) + (r[c] || 0);
      });
    });
    const ranked = Object.entries(totals).sort((a, b) => b[1] - a[1]);
    const top = ranked.slice(0, 6).map(([c]) => c);
    const rest = ranked.slice(6).map(([c]) => c);
    const normalised = rows.map((r) => {
      const out = { month: r.month, label: r.label, total: r.total || 0 };
      top.forEach((c) => {
        out[c] = r[c] || 0;
      });
      if (rest.length) {
        out.Other = (out.Other || 0) + rest.reduce((s, c) => s + (r[c] || 0), 0);
      }
      return out;
    });
    const renderedCats = rest.length && !top.includes("Other") ? [...top, "Other"] : top;

    // Trend stats — latest vs previous total
    let stats = null;
    if (normalised.length >= 2) {
      const last = normalised[normalised.length - 1];
      const prev = normalised[normalised.length - 2];
      const delta = (last.total || 0) - (prev.total || 0);
      const pct = prev.total ? Math.round((delta / prev.total) * 100) : 0;
      stats = { last, prev, delta, pct };
    }

    return { data: normalised, categories: renderedCats, stats };
  }, [timeline]);

  if (!data || data.length < 2) {
    return (
      <div className="cartiq-card" data-testid="spend-timeline-empty">
        <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
          — Your food timeline
        </div>
        <h2 className="font-display uppercase text-2xl mb-3">Trend unlocks at 2 months</h2>
        <p className="text-sm text-[#888] max-w-xl leading-relaxed">
          Keep uploading invoices as you order. The moment you have 2 months of data, this chart
          comes alive — showing you exactly how each category moves month over month, and whether
          your spend is rising or falling.
        </p>
      </div>
    );
  }

  const TrendIcon = stats?.delta > 0 ? TrendingUp : stats?.delta < 0 ? TrendingDown : Minus;
  const trendColor = stats?.delta > 0 ? "#E24B4A" : stats?.delta < 0 ? "#1D9E75" : "#888";
  const trendLabel =
    stats?.delta > 0
      ? "up from last month"
      : stats?.delta < 0
      ? "down from last month"
      : "flat vs last month";

  return (
    <div className="cartiq-card" data-testid="spend-timeline-card">
      <div className="flex items-start justify-between mb-4 gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] text-[#FF6B00] font-bold mb-1">
            — Your food timeline
          </div>
          <h2 className="font-display uppercase text-2xl md:text-3xl">
            Spend, month over month.
          </h2>
          <p className="text-sm text-[#888] mt-1">
            {data.length} {data.length === 1 ? "month" : "months"} of data · stacked by category
          </p>
        </div>
        {stats && (
          <div
            className="flex items-center gap-2 px-3 py-2 bg-[#161620] border border-[#2A2A35]"
            data-testid="spend-timeline-trend"
          >
            <TrendIcon className="w-4 h-4" style={{ color: trendColor }} />
            <div className="text-right">
              <div className="font-mono-num text-lg" style={{ color: trendColor }}>
                {stats.delta >= 0 ? "+" : ""}
                {formatShortINR(stats.delta)}
                <span className="text-[10px] ml-1 text-[#888]">({stats.pct}%)</span>
              </div>
              <div className="text-[9px] uppercase tracking-[0.22em] text-[#888]">
                {trendLabel}
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart data={data} margin={{ top: 10, right: 20, left: 0, bottom: 0 }}>
            <defs>
              {categories.map((c) => (
                <linearGradient
                  key={c}
                  id={`grad-${c.replace(/\s+/g, "")}`}
                  x1="0" y1="0" x2="0" y2="1"
                >
                  <stop
                    offset="0%"
                    stopColor={CATEGORY_COLORS[c] || "#555566"}
                    stopOpacity={0.85}
                  />
                  <stop
                    offset="100%"
                    stopColor={CATEGORY_COLORS[c] || "#555566"}
                    stopOpacity={0.25}
                  />
                </linearGradient>
              ))}
            </defs>
            <CartesianGrid stroke="#2A2A35" vertical={false} />
            <XAxis
              dataKey="label"
              tick={{ fill: "#888", fontSize: 11, fontFamily: "JetBrains Mono" }}
              axisLine={{ stroke: "#2A2A35" }}
              tickLine={false}
            />
            <YAxis
              tick={{ fill: "#888", fontSize: 10, fontFamily: "JetBrains Mono" }}
              tickFormatter={(v) => formatShortINR(v)}
              axisLine={{ stroke: "#2A2A35" }}
              tickLine={false}
              width={55}
            />
            <Tooltip content={<TimelineTooltip />} />
            <Legend
              iconType="square"
              iconSize={10}
              wrapperStyle={{
                fontSize: "11px",
                fontWeight: 700,
                textTransform: "uppercase",
                letterSpacing: "0.08em",
                paddingTop: "10px",
              }}
              formatter={(v) => <span style={{ color: "#F0EDE8" }}>{v}</span>}
            />
            {categories.map((c) => (
              <Area
                key={c}
                type="monotone"
                dataKey={c}
                stackId="1"
                stroke={CATEGORY_COLORS[c] || "#555566"}
                strokeWidth={1.5}
                fill={`url(#grad-${c.replace(/\s+/g, "")})`}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}
